import {Card, ColorSchemeType, FlagProps, FormControl} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useWatch} from 'react-hook-form';

import {all} from 'ramda';

import {Locale} from '@dms/api/base';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {RequirementChecklistItem} from '../../../components/RequirementChecklistItem/RequirementChecklistItem';
import {FormValues} from '../types/FormValues';
import {SettingsChecklistItem} from '../types/SettingsChecklistItem';
import {getSettingsChecklistItems} from '../utils/getSettingsChecklistItems';

const COMPLETE_COLOR_SCHEME: ColorSchemeType = 'green';
const INCOMPLETE_COLOR_SCHEME: ColorSchemeType = 'orange';

const isItemMet = (item: SettingsChecklistItem) => item.isMet;

interface SettingsChecklistProps extends TestIdProps {
  customerMessageId: string;
  control: FormControl<FormValues>;
  locales: Locale[];
}

export function SettingsChecklist(props: SettingsChecklistProps) {
  const [
    enabled,
    smsEnabled,
    emailEnabled,
    smsBodies,
    emailSubjects,
    emailBodies,
    emailCredentialsId,
    additionalSettings,
  ] = useWatch({
    control: props.control,
    name: [
      'enabled',
      'smsEnabled',
      'emailEnabled',
      'smsBodies',
      'emailSubjects',
      'emailBodies',
      'emailCredentialsId',
      'additionalSettings',
    ],
  });

  const items = getSettingsChecklistItems({
    customerMessageId: props.customerMessageId,
    additionalSettings,
    enabled,
    smsEnabled,
    emailEnabled,
    locales: props.locales,
    smsBodies,
    emailSubjects,
    emailBodies,
    emailCredentialsId,
  });

  const isComplete = all(isItemMet, items);

  const statusFlag: FlagProps = {
    label: isComplete
      ? i18n.t('page.settings.scheduledMessages.checklist.complete')
      : i18n.t('page.settings.scheduledMessages.checklist.incomplete'),
    colorScheme: isComplete ? COMPLETE_COLOR_SCHEME : INCOMPLETE_COLOR_SCHEME,
    isSubtle: true,
  };

  return (
    <Card
      variant="inlineGrey"
      title={i18n.t('page.settings.scheduledMessages.checklist.title')}
      parameters={[i18n.t('page.settings.scheduledMessages.checklist.description')]}
      flags={[statusFlag]}
      data-testid={suffixTestId('settingsChecklist', props)}
    >
      <VStack spacing={2}>
        {items.map((item) => (
          <RequirementChecklistItem
            key={item.id}
            title={item.title}
            isMet={item.isMet}
            data-testid={suffixTestId(`settingsChecklist-${item.id}`, props)}
          />
        ))}
      </VStack>
    </Card>
  );
}
